import React, { useState } from 'react';
import styled from 'styled-components';

interface CopyDayModalProps {
  isOpen: boolean;
  sourceDay: number;
  onSave: (targetDay: number) => void;
  onSaveMultiple: (targetDays: number[]) => void;
  onCancel: () => void;
  getDayName: (dayIndex: number) => string;
  onShowMessage: (message: string, isError?: boolean) => void;
}

const CopyDayModal: React.FC<CopyDayModalProps> = ({
  isOpen,
  sourceDay,
  onSave,
  onSaveMultiple,
  onCancel,
  getDayName,
  onShowMessage
}) => {
  const [selectedTargetDays, setSelectedTargetDays] = useState<number[]>([]);

  const handleSave = () => {
    if (selectedTargetDays.length === 0) {
      onShowMessage('복사할 요일을 선택해주세요.', true);
      return;
    }
    
    // 유효한 타겟 요일들 필터링
    const validTargetDays = selectedTargetDays.filter(targetDay => targetDay !== sourceDay);
    
    if (validTargetDays.length === 0) {
      onShowMessage('유효한 복사할 요일을 선택해주세요.', true);
      return;
    }
    
    // 복수 요일 복사 함수 호출
    onSaveMultiple(validTargetDays);
    
    // 모달 닫기
    onCancel();
  };

  // 모달이 열릴 때 선택된 요일들 초기화
  React.useEffect(() => {
    if (isOpen) {
      setSelectedTargetDays([]);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <ModalOverlay>
      <ModalContent>
        <ModalTitle>
          {getDayName(sourceDay)}요일 알람을 복사할 요일을 선택해주세요.
        </ModalTitle>
        <ModalDescription>
          복사할 요일의 기존 알람은 지워지고 해당 요일의 알람이 덮어쓰기 됩니다.
        </ModalDescription>
        
        <DaySelector>
          {[0, 1, 2, 3, 4, 5, 6].map(dayIndex => (
            <DayButton
              key={dayIndex}
              isSelected={selectedTargetDays.includes(dayIndex)}
              isDisabled={dayIndex === sourceDay}
              onClick={() => {
                if (dayIndex !== sourceDay) {
                  setSelectedTargetDays(prev => 
                    prev.includes(dayIndex) 
                      ? prev.filter(d => d !== dayIndex)
                      : [...prev, dayIndex]
                  );
                }
              }}
              disabled={dayIndex === sourceDay}
            >
              {getDayName(dayIndex)}
            </DayButton>
          ))}
        </DaySelector>
        
        <ButtonGroup>
          <ModalButton data-variant="modal-secondary" onClick={onCancel}>
            닫기
          </ModalButton>
          <ModalButton isPrimary onClick={handleSave}>
            복사
          </ModalButton>
        </ButtonGroup>
      </ModalContent>
    </ModalOverlay>
  );
};

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 24px;
  max-width: 400px;
  width: 90%;
  box-shadow: var(--modal-shadow);
  border: var(--modal-border);
`;

const ModalTitle = styled.h2`
  margin: 0 0 12px 0;
  font-size: 1.2rem;
  font-weight: bold;
  color: var(--color-text-primary);
  text-align: center;
`;

const ModalDescription = styled.p`
  margin: 0 0 20px 0;
  font-size: 0.9rem;
  color: var(--color-text-gray);
  text-align: center;
  line-height: 1.4;
`;

const DaySelector = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const DayButton = styled.button<{ isSelected: boolean; isDisabled: boolean }>`
  width: 40px;
  height: 40px;
  border: 2px solid ${props => {
    if (props.isDisabled) return 'var(--color-border)';
    return props.isSelected ? 'transparent' : 'var(--color-border)';
  }};
  border-radius: 50%;
  background: ${props => {
    if (props.isDisabled) return 'var(--color-panel)';
    return props.isSelected ? 'var(--color-accent-strong)' : 'var(--color-panel)';
  }};
  color: ${props => {
    if (props.isDisabled) return '#999';
    return props.isSelected ? 'white' : 'var(--color-text-gray)';
  }};
  cursor: ${props => props.isDisabled ? 'not-allowed' : 'pointer'};
  font-size: 0.9rem;
  font-weight: bold;
  transition: all 0.3s ease;
  box-sizing: border-box;
  
  /* 다크모드: 텍스트 색상 및 배경색 변경 */
  :root:not([data-theme='light']) & {
    color: ${props => {
      if (props.isDisabled) return '#999';
      return props.isSelected ? '#ffffff' : 'rgba(255, 255, 255, 0.5)';
    }};
    /* 다크모드: 선택되지 않았을 때 연한 테두리, 선택되었을 때 흰색 테두리 */
    border: 2px solid ${props => {
      if (props.isDisabled) return 'rgba(255, 255, 255, 0.3)';
      return props.isSelected ? '#ffffff' : 'rgba(255, 255, 255, 0.3)';
    }};
    /* 다크모드: 비선택일 때 연한 회색 배경 (선택했을 때 색상과 같은 색이지만 연하게) */
    background: ${props => {
      if (props.isDisabled) return 'var(--color-panel)';
      return props.isSelected ? 'var(--color-accent-strong)' : 'var(--color-accent-soft)';
    }};
  }
  
  &:hover {
    background: ${props => {
      if (props.isDisabled) return 'var(--color-panel)';
      return props.isSelected ? 'var(--color-accent)' : 'var(--color-border)';
    }};
  }
  
  &:focus {
    outline: none;
  }
  
  &:focus-visible {
    outline: none;
    border-color: #000;
    box-shadow: none;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const ModalButton = styled.button<{ isPrimary?: boolean; disabled?: boolean }>`
  background: ${props => {
    if (props.disabled) {
      return 'var(--color-panel)';
    }
    return props.isPrimary ? 'var(--color-accent-strong)' : '#bbb';
  }};
  color: ${props => {
    if (props.disabled) {
      return '#999';
    }
    return 'white';
  }};
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background 0.3s ease;
  
  &:hover {
    background: ${props => {
      if (props.disabled) {
        return 'var(--color-panel)';
      }
      return props.isPrimary ? 'var(--color-accent)' : '#999';
    }};
  }
  
  &:focus {
    outline: none;
  }
  
  &:focus-visible {
    outline: none;
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

export default CopyDayModal; 